import React, { ReactNode } from "react";
import { Card, Tag, Empty, Spin, Typography } from "antd";
import { InboxOutlined } from "@ant-design/icons";
// ── StatusTag ─────────────────────────────────────────────────
// Consistent tag for status fields across all tables.
// Maps common string statuses to colours automatically.
//
// Usage:
//   <StatusTag status="active" />
//   <StatusTag status="cancelled" />
//   <StatusTag status={order.status} label="Custom Label" />

const STATUS_MAP: Record<string, { color: string; label?: string }> = {
  // Positive
  active:           { color: "var(--mg-success)",  label: "Active" },
  completed:        { color: "var(--mg-success)",  label: "Completed" },
  paid:             { color: "var(--mg-success)",  label: "Paid" },
  verified:         { color: "var(--mg-success)",  label: "Verified" },
  approved:         { color: "var(--mg-success)",  label: "Approved" },
  resolved:         { color: "var(--mg-success)",  label: "Resolved" },
  fulfilled:        { color: "var(--mg-success)",  label: "Fulfilled" },
  // Neutral / processing
  pending:          { color: "var(--mg-primary)",  label: "Pending" },
  processing:       { color: "var(--mg-primary)",  label: "Processing" },
  scheduled:        { color: "var(--mg-primary)",  label: "Scheduled" },
  ordered:          { color: "var(--mg-primary)",  label: "Ordered" },
  uploaded:         { color: "var(--mg-info)",     label: "Uploaded" },
  assigned:         { color: "var(--mg-info)",     label: "Assigned" },
  // Warning
  paused:           { color: "var(--mg-warning)",  label: "Paused" },
  waiting:          { color: "var(--mg-warning)",  label: "Waiting" },
  unpaid:           { color: "var(--mg-warning)",  label: "Unpaid" },
  "partially paid": { color: "var(--mg-warning)",  label: "Partial" },
  review:           { color: "var(--mg-warning)",  label: "Review" },
  // Danger
  cancelled:        { color: "var(--mg-danger)",   label: "Cancelled" },
  canceled:         { color: "var(--mg-danger)",   label: "Cancelled" },
  failed:           { color: "var(--mg-danger)",   label: "Failed" },
  expired:          { color: "var(--mg-danger)",   label: "Expired" },
  inactive:         { color: "var(--mg-danger)",   label: "Inactive" },
  rejected:         { color: "var(--mg-danger)",   label: "Rejected" },
  voided:           { color: "var(--mg-danger)",   label: "Voided" },
  // Appointment-specific
  "in consultation":         { color: "var(--mg-info)",    label: "In Consultation" },
  arrived:                   { color: "var(--mg-info)",    label: "Arrived" },
  "no show":                 { color: "var(--mg-danger)",  label: "No Show" },
  "post consult completed":  { color: "var(--mg-success)", label: "Post Consult Complete" },
  started:                   { color: "var(--mg-info)",    label: "Started" },
  // Test / lab result statuses
  specimen_collected:        { color: "var(--mg-warning)", label: "Specimen Collected" },
  in_progress:               { color: "var(--mg-warning)", label: "In Progress" },
  results_available:         { color: "var(--mg-success)", label: "Results Available" },
  results_reviewed:          { color: "var(--mg-success)", label: "Results Reviewed" },
  analysis_failed:           { color: "var(--mg-danger)",  label: "Analysis Failed" },
  "post consult complete":   { color: "var(--mg-success)", label: "Post Consult Complete" },
  abnormal:                  { color: "var(--mg-danger)",  label: "Abnormal" },
  critical:                  { color: "var(--mg-danger)",  label: "Critical" },
  borderline:                { color: "var(--mg-warning)", label: "Borderline" },
  pending_review:            { color: "var(--mg-warning)", label: "Pending Review" },
  normal:                    { color: "var(--mg-success)", label: "Normal" },
  routine:                   { color: "var(--mg-info)",    label: "Routine" },
  urgent:                    { color: "var(--mg-warning)", label: "Urgent" },
  stat:                      { color: "var(--mg-danger)",  label: "STAT" },
  // Priority levels
  high:                      { color: "var(--mg-warning)", label: "High" },
  low:                       { color: "var(--mg-success)", label: "Low" },
  // Lab order shipping statuses
  delivered_to_customer:     { color: "var(--mg-success)", label: "Delivered to Customer" },
  delivered_to_lab:          { color: "var(--mg-success)", label: "Delivered to Lab" },
  shipped_to_customer:       { color: "var(--mg-primary)", label: "Shipped to Customer" },
  shipped_to_lab:            { color: "var(--mg-primary)", label: "Shipped to Lab" },
  sample_in_transit:         { color: "var(--mg-primary)", label: "In Transit" },
  kit_shipped:               { color: "var(--mg-primary)", label: "Kit Shipped" },
  kit_delivered:             { color: "var(--mg-success)", label: "Kit Delivered" },
  sample_delivered:          { color: "var(--mg-success)", label: "Sample Delivered" },
  delivery_failed:           { color: "var(--mg-danger)",  label: "Delivery Failed" },
  // Lab order / order-item statuses
  confirmed:                 { color: "var(--mg-primary)", label: "Confirmed" },
  received:                  { color: "var(--mg-info)",    label: "Received" },
  ready_for_processing:      { color: "var(--mg-primary)", label: "Ready for Processing" },
  // Shopify financial statuses
  authorized:                { color: "var(--mg-primary)", label: "Authorized" },
  partially_paid:            { color: "var(--mg-warning)", label: "Partial" },
  refunded:                  { color: "var(--mg-warning)", label: "Refunded" },
  partially_refunded:        { color: "var(--mg-warning)", label: "Partial Refund" },
  // Notification channels
  email:                     { color: "var(--mg-primary)", label: "Email" },
  sms:                       { color: "var(--mg-success)", label: "SMS" },
  push:                      { color: "var(--mg-warning)", label: "Push" },
  in_app:                    { color: "var(--mg-info)",    label: "In-App" },
  // File / content types
  audio:                     { color: "var(--mg-info)",    label: "Audio" },
  pdf:                       { color: "var(--mg-danger)",  label: "PDF" },
  global:                    { color: "var(--mg-primary)", label: "Global" },
  // User indicators
  you:                       { color: "var(--mg-warning)", label: "You" },
  // Supplement / plan types
  supplement:                { color: "var(--mg-info)",    label: "Supplement" },
  medication:                { color: "var(--mg-primary)", label: "Medication" },
  lifestyle:                 { color: "var(--mg-success)", label: "Lifestyle" },
  // Severity
  mild:                      { color: "var(--mg-warning)", label: "Mild" },
  moderate:                  { color: "var(--mg-warning)", label: "Moderate" },
  severe:                    { color: "var(--mg-danger)",  label: "Severe" },
  // Regimen / record lifecycle
  draft:                     { color: "var(--mg-info)",    label: "Draft" },
  archived:                  { color: "var(--mg-danger)",  label: "Archived" },
  ai_generated:              { color: "var(--mg-primary)", label: "AI Generated" },
  manual:                    { color: "var(--mg-info)",    label: "Manual" },
  // History / condition status
  chronic:                   { color: "var(--mg-danger)",  label: "Chronic" },
  acute:                     { color: "var(--mg-warning)", label: "Acute" },
  // Fulfillment
  unfulfilled:               { color: "var(--mg-warning)", label: "Unfulfilled" },
  partial:                   { color: "var(--mg-warning)", label: "Partial" },
  restocked:                 { color: "var(--mg-info)",    label: "Restocked" },
  // Form / review statuses
  reviewed:                  { color: "var(--mg-success)", label: "Reviewed" },
  // ⚠️ F6 — SPACE, NOT UNDERSCORE. `assigned_forms.status` spells this "In Progress",
  // and every lookup here goes through `.toLowerCase()` (line ~138), which yields
  // "in progress" — NOT the `in_progress` key above, which belongs to the lab/test
  // vocabulary. Without this row every form In Progress rendered as an unstyled default
  // tag in FormsTab, MedicalDashboard, FormsInTrayPanel and ReviewFormModal at once.
  // A separate row rather than normalising separators in the lookup: the two vocabularies
  // are genuinely different columns and DO differ in colour — this is `--mg-info` to match
  // `FormsInTrayPage`'s own `statusColor` map, while the lab's `in_progress` stays amber.
  // A practitioner sees both tray and tab; those two must agree with each other, not with
  // the lab table.
  "in progress":             { color: "var(--mg-info)", label: "In Progress" },
};

const SEMANTIC_TAG_STYLES: Record<string, { bg: string; border: string }> = {
  "var(--mg-success)": { bg: "var(--mg-success-bg)", border: "rgba(82,196,26,0.35)" },
  "var(--mg-danger)":  { bg: "var(--mg-danger-bg)",  border: "rgba(255,77,79,0.35)" },
  "var(--mg-warning)": { bg: "var(--mg-warning-bg)", border: "rgba(250,173,20,0.35)" },
  "var(--mg-primary)": { bg: "var(--mg-primary-bg)", border: "var(--mg-primary-border)" },
  "var(--mg-info)":    { bg: "var(--mg-info-bg)",    border: "rgba(24,144,255,0.35)" },
};

interface StatusTagProps {
  status: string;
  label?: string;
  size?: "small" | "default";
  /** Override the resolved color with a CSS var, e.g. "var(--mg-primary)" */
  color?: string;
}

const StatusTag: React.FC<StatusTagProps> = ({ status, label, size = "default", color: colorProp }) => {
  const key = status?.toLowerCase?.() ?? "";
  const mapped = STATUS_MAP[key];
  const color = colorProp ?? mapped?.color ?? "default";
  const text  = label ?? mapped?.label ?? status;

  // "default" means no custom colour — fall back to plain antd Tag styling.
  if (color === "default") {
    return (
      <Tag style={{ fontSize: size === "small" ? 11 : 12, padding: size === "small" ? "0 6px" : "1px 8px", fontWeight: 500 }}>
        {text}
      </Tag>
    );
  }

  const isVar = color.startsWith("var(");
  const tagStyles = isVar
    ? (SEMANTIC_TAG_STYLES[color] ?? { bg: "var(--mg-bg-subtle)", border: "var(--mg-border)" })
    : { bg: `${color}14`, border: `${color}40` };

  // Text colour: the background is always a *subtle tint* of the semantic colour
  // (CSS-var "*-bg" token or an 8%-opacity hex tint), composited over the page
  // background — so the readable text colour tracks the theme, not the tint.
  // `var(--mg-text)` resolves to near-black in light mode and near-white in dark
  // mode, i.e. black/white by mode. The semantic colour stays as the border +
  // background tint cue, never as the (previously low-contrast) text colour.

  return (
    <Tag
      style={{
        border:     `1px solid ${tagStyles.border}`,
        background:  tagStyles.bg,
        color:       "var(--mg-text)",
        fontSize:    size === "small" ? 11 : 12,
        padding:     size === "small" ? "0 6px" : "1px 8px",
        fontWeight:  500,
      }}
    >
      {text}
    </Tag>
  );
};

export default StatusTag;